import { type UndoType } from '@databases/analytics/src/types';
import {
  type DatabaseEntry,
  type DatabaseField,
  type DatabaseValue,
  type ImportedFileType
} from '@databases/types/database';
import { type Cheerio } from 'cheerio';
import { type Element } from 'domhandler';

export type RawFile = {
  name: string;
  mimeType: string;
  rawContent: string;
  size: number;
  type: ImportedFileType;
};

export type ParsedPasteFile = RawFile &
  (
    | {
        type: ImportedFileType.HTML;
        content: Array<Array<Cheerio<Element> | undefined>>;
      }
    | {
        type: ImportedFileType.CSV;
        content: string;
      }
    | {
        type: ImportedFileType.TEXT;
        content: string;
      }
  );

export enum ImportFieldMode {
  EXISTING = 'existing',
  CREATE = 'create',
  IGNORE = 'ignore'
}

export type ImportFieldReference =
  | { type: ImportFieldMode.EXISTING; fieldId: string }
  | { type: ImportFieldMode.CREATE; field: DatabaseField }
  | { type: ImportFieldMode.IGNORE };

export enum ImportEntryMode {
  EXISTING = 'existing',
  CREATE = 'create'
}

export type ImportEntryReference =
  | { type: ImportEntryMode.EXISTING; entryId: string }
  | { type: ImportEntryMode.CREATE; entry: DatabaseEntry };

export interface ImportSettings {
  /**
   * Which field to map each column to, in order.
   * A string references an existing database field by ID.
   * A DatabaseField object represents a database field that should be newly created.
   * null or undefined means that the field should be ignored.
   */
  fieldReferences: Array<ImportFieldReference | undefined>;
  /** For each row whether it should be imported or not. The default is true. */
  selectedRows: boolean[];

  /**
   * Which entry to map each row to, in order.
   * */
  entryReferences: Array<ImportEntryReference | undefined>;
  /**
   * For each column whether it should be imported or not. The default is true.
   * */
  selectedColumns: boolean[];
}

export interface ImportValues {
  /** A text representation of the cell content. */
  text: string;
  /** The cell parsed as a database value. Undefined if no field is selected for this column. */
  value?: DatabaseValue;
  /** Whether the pasted value should be applied to the target cell. Defaults to true when omitted. */
  shouldApplyPastedValue?: boolean;
  /**
   * Is true if the parsed database value is empty. The cause may be that the imported cell is empty or that it does not contain
   * any data that is compatible with the selected field type.
   */
  isTargetEmpty: boolean;
  targetFieldId: string | undefined;
  targetEntryId: string | undefined;
  targetIsMulti: boolean | undefined;
  isSourceEmpty: boolean | undefined;
}

export interface ImportResult {
  /** The number of columns in the imported data. */
  colCount: number;
  /** The parsed data rows and cells in the imported data. */
  rows: Array<{
    /** Whether the row is selected to be imported in the import settings. */
    isSelected: boolean;

    cols: Array<ImportValues>;
  }>;
}

export interface PastedItem {
  type: string;
  rawContent: string;
}

export type ParsedContentResult =
  | { success: true; file: ParsedPasteFile }
  | { success: false };

type PasteMessageTypeWithoutMessages =
  | Exclude<UndoType, 'successfulImport'>
  | 'cannotReadPastedContent'
  | 'unexpectedDataType'
  | 'readOnly'
  | 'pastedIntoSort';

type PasteMessageTypeWithMessages = 'entriesFiltered';

export type PasteMessageType =
  | PasteMessageTypeWithMessages
  | PasteMessageTypeWithoutMessages;

type PasteFlagTypes = 'warning' | 'error' | 'info';

interface PasteFlagTypesWithMessagesValues {
  type: PasteFlagTypes;
  actionType: PasteMessageTypeWithMessages;
  options: {
    messageValues: Record<string, string | number>;
    showIcon?: boolean;
  };
}

interface PasteFlagTypesWithoutMessageValues {
  type: PasteFlagTypes;
  actionType: PasteMessageTypeWithoutMessages;
  options?: {
    showIcon?: boolean;
  };
}

export type PasteFlagParams =
  | PasteFlagTypesWithMessagesValues
  | PasteFlagTypesWithoutMessageValues;

export type ShowPasteFlag = (data: PasteFlagParams[]) => void;

export interface PasteFlagConditions {
  isPartialPaste: boolean;
  isIncompatiblePasteContent: boolean;
  shouldFieldSettingUpdate: boolean;
  isPastingIntoSortedField: boolean;
}
